import React, { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stepper, Step, StepLabel, Paper,
  useTheme, Radio, RadioGroup, FormControlLabel, FormControl,
  Snackbar
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import Text from '../Text'

const ERP_SYSTEMS = {
  ixc: {
    name: 'IXC Provedor',
    variables: {
      pixCode: '#pix_copia_cola#',
      bankSlip: '#areceber_linha_digitavel#',
      price: '#areceber_valor_atual#',
      invoiceNumber: '#areceber_numero#',
      clientName: '#cliente_nome#',
      dueDate: '#areceber_data_vencimento#',
      contractId: '',
      providerName: '',
      providerCnpj: '',
      invoiceUrl: ''
    },
    previewVars: {
      pixCode: '00020126580014br.gov.bcb.pix0136a1b2c3d4-e5f6-7890-abcd-ef1234567890520400005303986540599.905802BR5925EMPRESA EXEMPLO6009SAO PAULO62070503***6304AB12',
      bankSlip: '23793.38128 60000.000003 00000.000400 1 84340000009990',
      price: '99.90',
      invoiceNumber: '217636',
      clientName: 'MARIA DA SILVA',
      dueDate: '15/04/2026',
      contractId: '1234'
    },
    templateNamePrefix: 'ixcsoft',
    instructions: 'No IXC, acesse a area de integracao WhatsApp e cole o texto gerado no campo de template da mensagem de cobranca.'
  },
  mkauth: {
    name: 'MK-AUTH',
    variables: {
      pixCode: '%copiaecola%',
      bankSlip: '%linhadig%',
      price: '%valortitulo%',
      invoiceNumber: '%numerotitulo%',
      clientName: '%nomeresumido%',
      dueDate: '',
      contractId: '%logincliente%',
      providerName: '%provedornome%',
      providerCnpj: '%provedorcnpj%',
      invoiceUrl: ''
    },
    previewVars: {
      pixCode: '00020126580014br.gov.bcb.pix0136a1b2c3d4-e5f6-7890-abcd-ef1234567890520400005303986540599.905802BR5925EMPRESA EXEMPLO6009SAO PAULO62070503***6304AB12',
      bankSlip: '23793.38128 60000.000003 00000.000400 1 84340000009990',
      price: '99.90',
      invoiceNumber: '5847',
      clientName: 'JOAO SANTOS',
      dueDate: 'Consulte',
      contractId: 'joao.santos'
    },
    templateNamePrefix: 'mk',
    instructions: 'No MK-AUTH, acesse Provedor > Configuracoes > Mensagens WhatsApp e cole o texto gerado no campo de template.'
  },
  sgp: {
    name: 'SGP',
    variables: {
      pixCode: '{codigopix}',
      bankSlip: '{linhadigitavel}',
      price: '{valor}',
      invoiceNumber: '{titulo}',
      clientName: '{cliente1}',
      dueDate: '{vencimento_original}',
      contractId: '{idcontrato}',
      providerName: '',
      providerCnpj: '',
      invoiceUrl: ''
    },
    previewVars: {
      pixCode: '00020126580014br.gov.bcb.pix0136a1b2c3d4-e5f6-7890-abcd-ef1234567890520400005303986540599.905802BR5925EMPRESA EXEMPLO6009SAO PAULO62070503***6304AB12',
      bankSlip: '23793.38128 60000.000003 00000.000400 1 84340000009990',
      price: '99.90',
      invoiceNumber: '12045',
      clientName: 'ANA OLIVEIRA',
      dueDate: '20/04/2026',
      contractId: '3021'
    },
    templateNamePrefix: 'sgp',
    instructions: 'No SGP, acesse Configuracoes > Integracoes > WhatsApp e cole o texto gerado no campo de mensagem.'
  }
}

const SCENARIOS = {
  fatura_disponivel: {
    name: 'Fatura disponivel',
    description: 'Notificacao de nova fatura gerada',
    whatsappTemplateSuffix: 'fatura_disponivel',
    previewMessage: (vars) => `Ola ${vars.clientName}! Sua fatura #${vars.invoiceNumber} no valor de R$ ${vars.price} esta disponivel. Efetue o pagamento ate o vencimento.`
  },
  fatura_atrasada: {
    name: 'Fatura em atraso',
    description: 'Cobranca de fatura vencida',
    whatsappTemplateSuffix: 'fatura_atrasada',
    previewMessage: (vars) => `Ola ${vars.clientName}! Sua fatura #${vars.invoiceNumber} no valor de R$ ${vars.price} esta em atraso. Regularize para evitar a suspensao do servico.`
  },
  fatura_vencendo_hoje: {
    name: 'Fatura vencendo hoje',
    description: 'Lembrete de vencimento no dia',
    whatsappTemplateSuffix: 'fatura_vencendo_hoje',
    previewMessage: (vars) => `Ola ${vars.clientName}! Sua fatura #${vars.invoiceNumber} no valor de R$ ${vars.price} vence hoje. Pague agora via PIX para baixa imediata.`
  }
}

const CONNECTION_TYPES = {
  qrcode: {
    name: 'QR Code (Baileys)',
    description: 'Mensagem enviada como texto/imagem normal'
  },
  meta_oficial: {
    name: 'Meta Oficial (Evolution API)',
    description: 'Template oficial da Meta com botoes'
  }
}

function generateTemplate({ erp, scenario, connectionType, profileId }) {
  const vars = ERP_SYSTEMS[erp].variables
  const prefix = ERP_SYSTEMS[erp].templateNamePrefix
  const scenarioData = SCENARIOS[scenario]

  let template = ''

  template += '%PaymentLink%\n'

  if (profileId) {
    template += `%ProfileId%${profileId}%/ProfileId%\n`
  }

  template += '%Pix%\n'
  template += '%PixCode%\n'
  template += `${vars.pixCode}\n`
  template += '%/PixCode%\n'
  template += '%PixQrCode%\n'
  template += '%/PixQrCode%\n'
  template += '%/Pix%\n'

  template += '%BankSlip%\n'
  template += `${vars.bankSlip}\n`
  template += '%/BankSlip%\n'

  template += '%Price%\n'
  template += `R$ ${vars.price}\n`
  template += '%/Price%\n'

  template += '%/PaymentLink%'

  if (connectionType === 'meta_oficial') {
    const templateName = `matriz_${prefix}_${scenarioData.whatsappTemplateSuffix}`

    template += '\n%WhatsappCloudApi%\n'
    template += '%Template%\n'
    template += `%Name%${templateName}%/Name%\n`

    if (vars.clientName) {
      template += `%VariableNumeric%${vars.clientName}%/VariableNumeric%\n`
    }

    if (vars.contractId) {
      template += `%VariableNumeric%${vars.contractId}%/VariableNumeric%\n`
    } else {
      template += '%VariableNumeric%Internet por assinatura%/VariableNumeric%\n'
    }

    if (vars.invoiceNumber) {
      template += `%VariableNumeric%${vars.invoiceNumber}%/VariableNumeric%\n`
    }

    if (vars.dueDate) {
      template += `%VariableNumeric%${vars.dueDate}%/VariableNumeric%\n`
    } else {
      template += '%VariableNumeric%Consulte%/VariableNumeric%\n'
    }

    if (vars.price) {
      template += `%VariableNumeric%${vars.price}%/VariableNumeric%\n`
    }

    template += '%ButtonUrl%%/ButtonUrl%\n'
    template += '%ButtonUrl%{{PAYMENT_LINK_CODE}}%/ButtonUrl%\n'
    template += '%/Template%\n'
    template += '%/WhatsappCloudApi%'
  }

  return template
}

function WhatsAppPreview({ erp, scenario, connectionType, profile }) {
  const previewVars = ERP_SYSTEMS[erp]?.previewVars || {}
  const scenarioData = SCENARIOS[scenario]

  if (!scenarioData) return null

  const previewText = scenarioData.previewMessage(previewVars)
  const linkCode = 'A1B2C3D4E5F6'
  const linkUrl = `https://hiperpay.app/pay/${linkCode}`

  return (
    <div className="flex justify-center">
      <div className="w-[320px] rounded-2xl overflow-hidden shadow-lg" style={{ backgroundColor: '#0b141a' }}>
        {/* Status bar */}
        <div className="flex items-center justify-between px-4 py-2" style={{ backgroundColor: '#1f2c34' }}>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full bg-gray-600" />
            <div>
              <p className="text-white text-sm font-medium">
                {profile?.name || 'Empresa'}
              </p>
              <p className="text-gray-400 text-xs">online</p>
            </div>
          </div>
        </div>

        {/* Chat area */}
        <div className="p-3 min-h-[280px] flex flex-col justify-end space-y-2">
          {connectionType === 'meta_oficial' ? (
            // Meta template preview
            <div className="max-w-[260px] rounded-lg p-3 self-start" style={{ backgroundColor: '#202c33' }}>
              <p className="text-white text-sm leading-relaxed">
                {previewText}
              </p>
              <div className="mt-3 border-t border-gray-600 pt-2">
                <a
                  className="text-center block text-sm font-medium py-1"
                  style={{ color: '#53bdeb' }}
                >
                  Acessar fatura
                </a>
              </div>
              <div className="border-t border-gray-600 pt-2">
                <a
                  className="text-center block text-sm font-medium py-1"
                  style={{ color: '#53bdeb' }}
                >
                  Pagar agora
                </a>
              </div>
              <div className="flex justify-end mt-1">
                <span className="text-gray-500 text-xs">21:48</span>
                <span className="ml-1 text-xs" style={{ color: '#53bdeb' }}>✓✓</span>
              </div>
            </div>
          ) : (
            // QR Code / Baileys preview — imagem + link
            <>
              <div className="max-w-[260px] rounded-lg overflow-hidden self-start" style={{ backgroundColor: '#202c33' }}>
                {/* Thumbnail preview */}
                <div className="w-full h-[140px] flex items-center justify-center" style={{ backgroundColor: profile?.primaryColor || '#03adec' }}>
                  <div className="text-center text-white">
                    {profile?.logoUrl ? (
                      <img
                        src={profile.logoUrl}
                        alt="Logo"
                        className="max-h-[50px] mx-auto mb-2"
                        onError={(e) => { e.target.style.display = 'none' }}
                      />
                    ) : (
                      <p className="font-bold text-lg">HiperPay</p>
                    )}
                    <p className="text-sm opacity-90">Pagamento disponivel</p>
                    <p className="font-bold text-xl">R$ {previewVars.price}</p>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-sm break-all" style={{ color: '#53bdeb' }}>
                    {linkUrl}
                  </p>
                  <div className="flex justify-end mt-1">
                    <span className="text-gray-500 text-xs">21:48</span>
                    <span className="ml-1 text-xs" style={{ color: '#53bdeb' }}>✓✓</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default function TemplateGenerator({ open, onClose, profiles, profileMode }) {
  const theme = useTheme()
  const [activeStep, setActiveStep] = useState(0)
  const [connectionType, setConnectionType] = useState('')
  const [selectedErp, setSelectedErp] = useState('')
  const [selectedScenario, setSelectedScenario] = useState('')
  const [selectedProfileId, setSelectedProfileId] = useState('')
  const [message, setMessage] = useState('')

  const steps = ['Conexao', 'Sistema', 'Cenario', 'Template']

  const handleNext = () => {
    if (activeStep === 0 && !connectionType) return
    if (activeStep === 1 && !selectedErp) return
    if (activeStep === 2 && !selectedScenario) return
    setActiveStep(prev => prev + 1)
  }

  const handleBack = () => {
    setActiveStep(prev => prev - 1)
  }

  const handleClose = () => {
    setActiveStep(0)
    setConnectionType('')
    setSelectedErp('')
    setSelectedScenario('')
    setSelectedProfileId('')
    onClose()
  }

  const selectedProfile = profiles.find(p => p._id === selectedProfileId) || profiles.find(p => p.isDefault)

  const generatedTemplate = activeStep === 3
    ? generateTemplate({
        erp: selectedErp,
        scenario: selectedScenario,
        connectionType,
        profileId: profileMode ? (selectedProfileId || selectedProfile?._id || '') : ''
      })
    : ''

  const charCount = generatedTemplate.length

  const copyTemplate = () => {
    navigator.clipboard.writeText(generatedTemplate)
    setMessage('Template copiado!')
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>Gerar Template para ERP</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} className="py-4">
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <div className="py-4 min-h-[300px]">
            {/* Step 0: Tipo de conexao */}
            {activeStep === 0 && (
              <div className="space-y-3">
                <Text className="font-medium text-lg">Qual o tipo de conexao da instancia?</Text>
                <FormControl>
                  <RadioGroup value={connectionType} onChange={(e) => setConnectionType(e.target.value)}>
                    {Object.entries(CONNECTION_TYPES).map(([key, ct]) => (
                      <FormControlLabel
                        key={key}
                        value={key}
                        control={<Radio />}
                        label={
                          <div>
                            <Text className="font-medium">{ct.name}</Text>
                            <Text className="text-sm opacity-60">{ct.description}</Text>
                          </div>
                        }
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </div>
            )}

            {/* Step 1: Selecionar ERP */}
            {activeStep === 1 && (
              <div className="space-y-3">
                <Text className="font-medium text-lg">Qual sistema integrado?</Text>
                <FormControl>
                  <RadioGroup value={selectedErp} onChange={(e) => setSelectedErp(e.target.value)}>
                    {Object.entries(ERP_SYSTEMS).map(([key, erp]) => (
                      <FormControlLabel
                        key={key}
                        value={key}
                        control={<Radio />}
                        label={<Text className="font-medium">{erp.name}</Text>}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </div>
            )}

            {/* Step 2: Selecionar cenario */}
            {activeStep === 2 && (
              <div className="space-y-4">
                <Text className="font-medium text-lg">Qual o cenario da mensagem?</Text>
                <FormControl>
                  <RadioGroup value={selectedScenario} onChange={(e) => setSelectedScenario(e.target.value)}>
                    {Object.entries(SCENARIOS).map(([key, sc]) => (
                      <FormControlLabel
                        key={key}
                        value={key}
                        control={<Radio />}
                        label={
                          <div>
                            <Text className="font-medium">{sc.name}</Text>
                            <Text className="text-sm opacity-60">{sc.description}</Text>
                          </div>
                        }
                      />
                    ))}
                  </RadioGroup>
                </FormControl>

                {profileMode && profiles.length > 0 && (
                  <div className="pt-2">
                    <Text className="font-medium mb-2">Profile de branding (opcional):</Text>
                    <FormControl>
                      <RadioGroup value={selectedProfileId} onChange={(e) => setSelectedProfileId(e.target.value)}>
                        {profiles.map((profile) => (
                          <FormControlLabel
                            key={profile._id}
                            value={profile._id}
                            control={<Radio />}
                            label={
                              <div className="flex items-center space-x-2">
                                {profile.primaryColor && (
                                  <div
                                    className="w-4 h-4 rounded-full border"
                                    style={{ backgroundColor: profile.primaryColor }}
                                  />
                                )}
                                <Text className="font-medium">
                                  {profile.name} {profile.isDefault ? '(Padrao)' : ''}
                                </Text>
                              </div>
                            }
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Template gerado + Preview */}
            {activeStep === 3 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Coluna esquerda: Template */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Text className="font-medium text-lg">Template gerado</Text>
                    <div className="flex items-center space-x-3">
                      <Text className="text-sm opacity-60">{charCount} chars</Text>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={copyTemplate}
                      >
                        Copiar
                      </Button>
                    </div>
                  </div>

                  {charCount > 800 && (
                    <Paper elevation={0} className="p-3 rounded" style={{ backgroundColor: '#fff3e0' }}>
                      <Text className="text-sm" style={{ color: '#e65100' }}>
                        Atencao: {charCount} caracteres. O IXC pode cortar templates acima de ~800 caracteres.
                      </Text>
                    </Paper>
                  )}

                  <Paper
                    elevation={0}
                    className="p-3 rounded font-mono text-xs overflow-auto max-h-[350px]"
                    style={{
                      backgroundColor: theme.isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all'
                    }}
                  >
                    {generatedTemplate}
                  </Paper>

                  <Paper elevation={0} className="p-3 rounded" style={{ backgroundColor: theme.isDark ? 'rgba(255,255,255,0.05)' : '#e3f2fd' }}>
                    <Text className="text-sm font-medium mb-1">Onde colar:</Text>
                    <Text className="text-sm opacity-80">
                      {ERP_SYSTEMS[selectedErp]?.instructions}
                    </Text>
                    {connectionType === 'meta_oficial' && (
                      <Text className="text-sm opacity-80 mt-2">
                        O template <strong>{`matriz_${ERP_SYSTEMS[selectedErp]?.templateNamePrefix}_${SCENARIOS[selectedScenario]?.whatsappTemplateSuffix}`}</strong> deve estar aprovado no WhatsApp Business Manager.
                      </Text>
                    )}
                  </Paper>
                </div>

                {/* Coluna direita: Preview WhatsApp */}
                <div className="space-y-3">
                  <Text className="font-medium text-lg text-center">Preview</Text>
                  <Text className="text-sm opacity-60 text-center">
                    Visualizacao de como o cliente recebera a mensagem
                  </Text>
                  <WhatsAppPreview
                    erp={selectedErp}
                    scenario={selectedScenario}
                    connectionType={connectionType}
                    profile={selectedProfile}
                  />
                </div>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Fechar</Button>
          {activeStep > 0 && (
            <Button onClick={handleBack}>Voltar</Button>
          )}
          {activeStep < 3 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                (activeStep === 0 && !connectionType) ||
                (activeStep === 1 && !selectedErp) ||
                (activeStep === 2 && !selectedScenario)
              }
            >
              Proximo
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!message}
        autoHideDuration={3000}
        onClose={() => setMessage('')}
        message={message}
      />
    </>
  )
}
